import React, { useState, useRef, useEffect } from "react";
import {
  Modal,
  TextInput,
  Button,
  Group,
  Stack,
  Title,
  Alert,
  Text,
  Center,
  Box,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconVideo,
  IconPlayerRecord,
  IconPlayerStop,
} from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";

const RecordPitchModal = ({
  opened,
  onClose,
  onSubmit,
  videoId,
  onSubmitSuccess,
  initialChannelId,
}) => {
  const { getVerifiedJWT } = useAuth();
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [totalRecordingTime, setTotalRecordingTime] = useState(0);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [channelId, setChannelId] = useState(null);
  const [recordingMimeType, setRecordingMimeType] = useState("");

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const videoRef = useRef(null);
  const previewRef = useRef(null);
  const timerRef = useRef(null);

  // Fetch channel ID when modal opens
  useEffect(() => {
    const fetchChannelId = async () => {
      if (!opened) {
        setChannelId(null);
        return;
      }

      setError("");

      try {
        const jwt = await getVerifiedJWT();
        if (!jwt) {
          throw new Error("Authentication failed");
        }

        const response = await fetch(
          getApiEndpoint("/v1/pitch-review/my-channel"),
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.message || "Failed to fetch channel information"
          );
        }

        const result = await response.json();
        const channelId = result.data?.id;

        if (!channelId) {
          throw new Error("No channel ID received from server");
        }

        setChannelId(channelId);
      } catch (err) {
        setError(err.message || "Failed to load channel information.");
      }
    };

    if (initialChannelId) {
      setChannelId(initialChannelId);
    } else {
      fetchChannelId();
    }
    //    eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, getVerifiedJWT]);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      stopRecording();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startRecording = async () => {
    try {
      setError("");

      // Get user media (camera and microphone)
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      // Create media recorder with format fallback
      let mimeType = "video/webm;codecs=vp9";
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = "video/webm;codecs=vp8";
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = "video/webm";
          if (!MediaRecorder.isTypeSupported(mimeType)) {
            mimeType = "video/mp4";
            if (!MediaRecorder.isTypeSupported(mimeType)) {
              mimeType = ""; // Let browser choose
            }
          }
        }
      }

      const mediaRecorder = new MediaRecorder(
        stream,
        mimeType ? { mimeType } : undefined
      );

      mediaRecorderRef.current = mediaRecorder;
      const chunks = [];

      // Store the actual MIME type being used
      const actualMimeType = mediaRecorder.mimeType || mimeType || "video/webm";
      setRecordingMimeType(actualMimeType);

      mediaRecorder.ondataavailable = event => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        if (chunks.length === 0) {
          setError("No video data was recorded. Please try recording again.");
          return;
        }

        // Create blob without specifying type first, let browser decide
        const blob = new Blob(chunks);
        setRecordedBlob(blob);
        const url = URL.createObjectURL(blob);
        setRecordedUrl(url);

        // Stop the stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }

        // Force video element to reload once the URL is set
        setTimeout(() => {
          if (previewRef.current) {
            previewRef.current.load();
          }
        }, 50);
      };

      // Start recording with specific timeslice for better data collection
      mediaRecorder.start(1000);
      setIsRecording(true);

      setTotalRecordingTime(0);

      // Start timer countdown
      timerRef.current = setInterval(() => {
        setTotalRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (err) {
      setError("Failed to access camera/microphone. Please check permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    setIsRecording(false);
  };

  const handleSubmit = async () => {
    if (!recordedBlob) {
      setError("Please record a pitch video first.");
      return;
    }

    if (!channelId) {
      setError("Channel information not ready. Please wait or try again.");
      return;
    }

    setIsSubmitting(true);
    try {
      // Get JWT token for API authentication
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        throw new Error("Authentication failed");
      }

      // Determine the correct file extension and content type
      const contentType =
        recordingMimeType || recordedBlob.type || "video/webm";
      const fileExtension = contentType.includes("mp4") ? "mp4" : "webm";
      const fileName = `pitch-recording-${Date.now()}.${fileExtension}`;
      const file = new File([recordedBlob], fileName, { type: contentType });

      // Step 1: Get presigned URL for video upload
      const apiResponse = await fetch(
        getApiEndpoint("/v1/pitch-review/video/upload"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            channel_id: channelId,
            video_file_name: fileName,
            video_file_content_type: contentType,
            video_id: videoId,
            response_text: notes,
          }),
        }
      );

      if (!apiResponse.ok) {
        const errorData = await apiResponse.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to get upload URL");
      }

      const result = await apiResponse.json();
      const responseData = result.data;
      const presignedUrl =
        responseData.presign?.uploadUrl || responseData.uploadUrl;

      if (!presignedUrl) {
        throw new Error("No presigned URL received from server");
      }

      // Step 2: Upload video to presigned URL
      const uploadResponse = await fetch(presignedUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": contentType,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.status}`);
      }

      // Call parent handler for any additional processing (e.g., refreshing data)
      if (onSubmit) {
        try {
          await onSubmit({ success: true });
        } catch (parentError) {
          // Parent handler failed, but video was uploaded successfully
        }
      }

      // Call success handler to show the "Video Sent!" modal
      if (onSubmitSuccess) {
        onSubmitSuccess();
      }

      // Reset form on successful submission
      handleClose();
    } catch (err) {
      setError(err.message || "Failed to submit pitch recording.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    stopRecording();

    // Clean up URLs
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }

    // Reset state
    setRecordedBlob(null);
    setRecordedUrl(null);
    setNotes("");
    setError("");

    setTotalRecordingTime(0);
    setIsSubmitting(false);

    onClose();
  };

  const retryRecording = () => {
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    setRecordedBlob(null);
    setRecordedUrl(null);

    setTotalRecordingTime(0);
    setError("");
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Title order={3} fw={600}>
          Record Your Pitch
        </Title>
      }
      size="lg"
      padding="xl"
    >
      <Stack gap="xl">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error"
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        )}

        {/* Video Recording Area */}
        <Box
          style={{
            backgroundColor: "#000",
            borderRadius: "8px",
            aspectRatio: "16/9",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {!recordedUrl ? (
            // Live camera feed during recording or before recording
            <video
              ref={videoRef}
              autoPlay
              muted
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          ) : (
            // Recorded video preview
            <video
              key={recordedUrl} // Force re-render when URL changes
              ref={previewRef}
              src={recordedUrl}
              controls
              controlsList="nodownload"
              preload="metadata"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
              onLoadedMetadata={() => {
                // Set video to 0.2 seconds to avoid showing black frame
                if (previewRef.current) {
                  previewRef.current.currentTime = 0.2;
                }
              }}
              onError={e => {
                setError("Failed to load recorded video for preview");
              }}
            />
          )}

          {/* Recording indicator */}
          {isRecording && (
            <Box
              style={{
                position: "absolute",
                top: "16px",
                right: "16px",
                background: "rgba(255, 0, 0, 0.9)",
                color: "white",
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "12px",
                fontWeight: "bold",
              }}
            >
              REC{" "}
              {`${Math.floor(totalRecordingTime / 60)}:${(totalRecordingTime % 60).toString().padStart(2, "0")}`}
            </Box>
          )}

          {/* Initial state overlay */}
          {!streamRef.current && !recordedUrl && (
            <Center
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.8)",
              }}
            >
              <Stack align="center" gap="sm">
                <IconVideo size={48} color="white" />
                <Text c="white" ta="center">
                  Click "Start Recording" to begin
                </Text>
              </Stack>
            </Center>
          )}
        </Box>

        <Group justify="center" gap="md">
          {!isRecording && !recordedUrl && (
            <Button
              leftSection={<IconPlayerRecord size={16} />}
              onClick={startRecording}
              color="red"
              size="md"
            >
              Start Recording
            </Button>
          )}

          {isRecording && (
            <Button
              leftSection={<IconPlayerStop size={16} />}
              onClick={stopRecording}
              color="red"
              variant="filled"
              size="md"
            >
              Stop Recording
            </Button>
          )}

          {recordedUrl && (
            <Group gap="sm">
              <Button
                onClick={retryRecording}
                variant="outline"
                size="md"
                leftSection={<IconPlayerRecord size={16} />}
              >
                Record Again
              </Button>
            </Group>
          )}
        </Group>

        {/* Notes field */}
        <TextInput
          label="Add Note (Optional)"
          placeholder="Add any additional notes about your pitch..."
          value={notes}
          onChange={e => setNotes(e.target.value)}
          disabled={isSubmitting}
        />

        {/* Submit buttons */}
        <Group justify="flex-end" gap="sm">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!recordedBlob}
            loading={isSubmitting}
          >
            Submit Pitch
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default RecordPitchModal;
